<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { isFinishedRunStatus, type RunHistory } from "@/types";
import { useChildRuns } from "@/queries/runs";
import { formatDuration, formatRunTime, getRunStatusBadgeClass, getRunStatusLabel } from "../utils/runFormatters";

const props = withDefaults(defineProps<{
  run: RunHistory;
  selectedRunId?: string;
  showProject?: boolean;
  projectNameById?: Map<string, string>;
  ancestors?: string[];
  /** True when this row is a nested child — draws the hierarchy rail, stub and junction dot.
   *  Top-level rows (rendered straight into the list) don't get one. */
  nested?: boolean;
  /** True when this is the last child of its parent — caps the vertical rail at the junction
   *  (an "L") so nothing hangs below the final child. */
  isLast?: boolean;
  /** Grouped view: show the expander so children nest under this row. When false (flat view),
   *  children are their own top-level rows, so no expander. */
  grouped?: boolean;
}>(), { ancestors: () => [], grouped: true });
const emit = defineEmits<{ select: [run: RunHistory] }>();
const expanded = ref(false);
const page = ref(0);
const runId = computed(() => props.run.id);
const lineage = computed(() => [...props.ancestors, props.run.id]);
const canExpand = computed(() => props.run.services.some(s => s.serviceType === "subWorkflow"));

// Top-level rows that have children open by default in grouped mode, so entering grouped immediately
// shows the hierarchy rather than a list of lone parents. Nested rows stay collapsed so grandchildren
// load on demand, not in a cascade. The component instance is reused across a grouped toggle (keyed by
// run id), so a watcher — not just the initial value — is needed to re-open on re-entry.
const autoExpands = computed(() => props.grouped && !props.nested && canExpand.value);
expanded.value = autoExpands.value;
watch(autoExpands, (open) => { expanded.value = open; });
const { childRuns, childRunsTotal, childRunsLoading, childRunsError, retryChildRuns, pageSize } =
  useChildRuns(runId, page, expanded, computed(() => !isFinishedRunStatus(props.run.status)));
// Never let malformed historical links recursively display the same ancestor.
const children = computed(() => childRuns.value.filter(run => !lineage.value.includes(run.id)));
</script>

<template>
  <div class="history-branch" :class="{ nested, 'is-last': isLast }">
    <div class="list-group-item list-group-item-action" :class="{ active: selectedRunId === run.id }">
      <button type="button" class="run-select text-start border-0 bg-transparent p-0 w-100" @click="emit('select', run)">
        <span class="d-flex justify-content-between align-items-center mb-1">
          <span class="badge" :class="getRunStatusBadgeClass(run.status)">
            <span v-if="run.status === 'Running' || run.status === 'Pending'" class="spinner-border spinner-border-sm me-1" style="width: 0.6rem; height: 0.6rem" role="status"></span>
            {{ getRunStatusLabel(run.status) }}
          </span>
          <small class="text-muted fw-medium"><i class="bi bi-clock"></i> {{ run.durationInMilliSeconds ? formatDuration(run.durationInMilliSeconds) : '' }}</small>
        </span>
        <span class="d-block small fw-medium mb-1">{{ run.workflowName }}</span>
        <span v-if="run.parentRunId" class="d-block small text-muted mb-1">
          <i class="bi bi-arrow-return-right me-1"></i>Called by {{ run.parentServiceName || run.parentServiceId }}
        </span>
        <span v-if="showProject" class="d-block small text-muted mb-1">
          <i class="bi bi-folder2-open me-1"></i>{{ projectNameById?.get(run.runningProjectId ?? '') || '—' }}
        </span>
        <span class="d-block small text-muted mb-1">{{ formatRunTime(run.startedAt) }}</span>
        <span v-if="run.errors?.length" class="d-block small text-danger text-truncate">{{ run.errors[0]?.message ?? '' }}</span>
      </button>
      <button v-if="canExpand && grouped" type="button" class="child-toggle btn btn-sm d-block ms-auto p-0 mt-1 small" :aria-expanded="expanded" @click="expanded = !expanded">
        <i class="bi" :class="expanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
        {{ expanded ? 'Hide child runs' : 'Show child runs' }}
      </button>
    </div>
    <div v-if="expanded" class="child-runs ms-2 ps-4">
      <div v-if="childRunsError && !childRuns.length" class="small p-2" role="alert">
        {{ childRunsError.userMessage }}
        <button type="button" class="bb-btn-secondary-cta btn-sm" @click="retryChildRuns()">Retry</button>
      </div>
      <small v-else-if="childRunsLoading && !childRuns.length" class="d-block p-2" role="status">Loading child runs…</small>
      <small v-else-if="!childRuns.length" class="d-block p-2 text-muted">No child runs yet.</small>
      <RunHistoryRow v-for="(child, i) in children" :key="child.id" :run="child" :selected-run-id="selectedRunId" :show-project="showProject" :project-name-by-id="projectNameById" :ancestors="lineage" nested :is-last="i === children.length - 1" :grouped="grouped" @select="emit('select', $event)" />
      <div v-if="childRunsTotal > pageSize || page > 0" class="d-flex align-items-center justify-content-between gap-1 py-2">
        <button type="button" class="bb-btn-secondary-cta btn-sm" :disabled="page === 0 || childRunsLoading" @click="page--">Prev</button>
        <small>{{ page + 1 }} / {{ Math.max(1, Math.ceil(childRunsTotal / pageSize)) }}</small>
        <button type="button" class="bb-btn-secondary-cta btn-sm" :disabled="(page + 1) * pageSize >= childRunsTotal || childRunsLoading" @click="page++">Next</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.run-select { color: inherit; }

/* Only the muted→orange colour Bootstrap utilities can't express; size/layout are utility classes. */
.child-toggle {
  color: var(--bs-secondary-color);
  text-decoration: none;
}
.child-toggle:hover {
  color: var(--bb-orange, var(--bs-primary));
}

/* Hierarchy lines drawn in CSS (no geometry observer): each nested branch owns its rail, so the
   trunk caps at the last child as an "L" instead of running to the panel bottom. The elbow sits
   1.5rem from the branch top (the status-line middle). The rail sits centred in the .ps-4 gutter
   (left:-0.75rem = middle of the 1.5rem padding), so there's a smaller gap to the parent's left
   edge and a larger gap across to the child cards. */
.history-branch.nested {
  position: relative;
}
/* Vertical rail — full height by default, so siblings form one unbroken trunk. */
.history-branch.nested::before {
  content: "";
  position: absolute;
  left: -1.0375rem;
  top: 0;
  bottom: 0;
  border-left: 1px solid var(--bs-border-color);
}
/* First child: stop flush at the parent card's bottom edge (its bottom margin) without climbing over it. */
.history-branch.nested:first-of-type::before {
  top: -0.0625rem;
}
/* Last child: cap the rail at its junction so the trunk ends in an "L". */
.history-branch.nested.is-last::before {
  bottom: auto;
  height: 1.5rem;
}
/* Only child (first AND last): climb to the parent edge AND cap at the junction. */
.history-branch.nested.is-last:first-of-type::before {
  top: -0.0625rem;
  height: 1.5625rem; /* 0.0625rem climb + 1.5rem to the junction */
}
/* Horizontal stub — rail across to the child card's left edge. */
.history-branch.nested::after {
  content: "";
  position: absolute;
  left: -1.0375rem;
  top: 1.5rem;
  width: 1.0375rem;
  border-top: 1px solid var(--bs-border-color);
}
/* Orange junction dot at the elbow, on the card's free ::before. */
.history-branch.nested > .list-group-item {
  position: relative;
}
.history-branch.nested > .list-group-item::before {
  content: "";
  position: absolute;
  left: -1.0375rem;
  top: 1.5rem;
  width: 6px;
  height: 6px;
  margin-left: -3px;
  margin-top: -3px;
  border-radius: 50%;
  background: var(--bs-warning);
  border: 1px solid var(--bs-body-bg);
  pointer-events: none;
  z-index: 1;
}
</style>
